import { Typography, useToast } from "@hellocarbo/lasagne";
import { use, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  useActionData,
  useLoaderData,
  useSubmit,
} from "react-router";
import invariant from "tiny-invariant";

import {
  DownloadAttestation,
  Referral,
  SharingStatus,
} from "@/components/Communicate";
import Analyses from "@/components/Communicate/Analyses";
import Medal from "@/components/Communicate/Medal";
import { UserContext } from "@/contexts";
import { TReviewCommunicate, TReviewSharingStatus } from "@/types";

import { useCommunicateContext } from "./Communicate";

export async function loader({ params }: LoaderFunctionArgs) {
  const { reviewId } = params;
  if (reviewId === "free-trial") {
    return {
      review: null,
    };
  }

  invariant(reviewId, "No review id found!");

  const reviewResponse = await fetch(
    `/api/client/reviews/${reviewId}/communicate`
  );

  if (reviewResponse.status !== 200) {
    throw new Error("Failed to load the review data");
  }

  const { data: review } = (await reviewResponse.json()) as {
    data: TReviewCommunicate;
  };

  return { review };
}

export async function action({ request, params }: ActionFunctionArgs) {
  const { reviewId } = params;
  invariant(reviewId, "No review id found!");

  const formData = await request.formData();
  const status = formData.get("status");

  const response = await fetch(
    `/api/client/reviews/${reviewId}/sharing_status`,
    {
      method: "PATCH",
      body: JSON.stringify({ sharing_status: status }),
      headers: { "Content-Type": "application/json" },
    }
  );

  if (response.status !== 200) {
    return { ok: false };
  }

  const { data: review } = (await response.json()) as {
    data: TReviewCommunicate;
  };

  return { ok: true, newStatus: review.sharing_status };
}

function ReviewContent() {
  const { t } = useTranslation(["communicate", "formatter"]);
  const { reviews } = useCommunicateContext();
  const { review } = useLoaderData<typeof loader>();
  const { user } = use(UserContext);
  const submit = useSubmit();
  const actionData = useActionData<typeof action>();
  const { toast } = useToast();

  useEffect(() => {
    if (actionData?.newStatus === "private") {
      toast({
        title: t("communicate:sharingStatus.publicFootprintDisabled"),
        toastType: "simple",
        intent: "positive",
      });
    }
  }, [actionData]);

  const isFreeTrial = user?.company.subscriptionType === "start";

  return (
    <>
      <div className="col-span-full">
        {review ? (
          <Typography step="2" as="h2" weight="bold" className="mb-sm">
            {t("communicate:sharingStatus.footprintFromTo", {
              start: t("formatter:date", {
                value: new Date(
                  reviews.find((r) => r.id === review?.id)?.period_start ?? ""
                ),
              }),
              end: t("formatter:date", {
                value: new Date(
                  reviews.find((r) => r.id === review?.id)?.period_end ?? ""
                ),
              }),
            })}
          </Typography>
        ) : null}
      </div>
      <div className="gap-sm lg:gap-y-md flex flex-col">
        <SharingStatus
          status={review?.sharing_status || ("private" as TReviewSharingStatus)}
          url={review?.public_review_url}
          updateSharingStatus={(newStatus) =>
            submit({ status: newStatus }, { method: "patch" })
          }
        />
        <Medal />
        {(review?.certificate_url || isFreeTrial) && (
          <DownloadAttestation url={review?.certificate_url} />
        )}
        {(review?.pdf_restitution_url ||
          review?.trajectories_url ||
          isFreeTrial) && (
          <Analyses
            pdfRestitutionUrl={review?.pdf_restitution_url}
            trajectoriesUrl={review?.trajectories_url}
          />
        )}
        {user?.company.subscriptionType !== "start" && <Referral />}
      </div>
    </>
  );
}
export default ReviewContent;
